<template>
  <div class="card"
       id="detail-schedule">
    <header class="header-card">
      <p>
        {{ $t('detail.schedule.title') }}
      </p>
    </header>
    <div class="card-content">
      <div v-if="isMultiPayeur">
        <p v-for="(payeur, index) in payeurs" :key="index">
          {{payeur.nomCommercial}} - {{getQuotePart(payeur)}}
          <span v-if="payeur.typeRepartition !== constantes.MONTANT"
                class="has-text-weight-bold">
            {{ ': ' + getMontantPayeur(payeur)}}
          </span>
        </p>
        {{$t('detail.schedule.totalAmount')}}
        {{ getTotalEcheanchiers | formatDevise }}
      </div>
      <qd-collapse
            v-for="echeance in echeanciers"
            :key="echeance.referenceEcheancier"
            class="card mgt-20px"
            animation="slide"
            :aria-id="echeance.referenceEcheancier">
            <template #trigger="props">
                <div
                    class="card-header has-background-background"
                    role="button"
                    :aria-controls="echeance.referenceEcheancier"
                    :aria-expanded="props.open">
                    <a class="card-header-icon">
                        <b-icon
                            :icon="props.open && showEcheancier(echeance.referenceEcheancier) ?
                            'chevron-down' : 'chevron-right'"
                            :class="{'disabled' : !showEcheancier(echeance.referenceEcheancier)}" >
                        </b-icon>
                    </a>
                    <div class="columns width-100percent is-marginless">
                      <div class="column is-3">
                        <p class="has-text-weight-bold">
                          {{ $t('detail.schedule.table.product') }}
                        </p>
                        <div
                            class="pdl-4"
                            v-for="(libelles, index) in getLibelle(echeance.referenceEcheancier)"
                            :key="index">
                          <p class="ellipsis">
                            {{ libelles.libelleProduit }}
                          </p>
                          <p class="ellipsis is-size-7">{{ libelles.libellePatri }}</p>
                          <p v-if="libelles.dateDebut" class="is-size-7">
                            {{ getDate(libelles.dateDebut) }}
                          </p>
                        </div>
                      </div>
                      <div class="column is-2"
                        v-if="!showEcheancier(echeance.referenceEcheancier) && isNotRegie">
                          <p class="has-text-weight-bold right">
                            {{ $t('echeancierFacturation.tabs.fournisseur') }}
                          </p>
                          <div class="right mg-10px">
                            {{ getFournisseur(echeance.referenceEcheancier) }}
                          </div>
                      </div>
                      <div class="column is-2" v-else></div>
                      <div class="column is-2">
                        <p class="has-text-weight-bold right">
                          {{ $t('detail.schedule.table.amount') }}
                        </p>
                        <div class="right mg-10px">
                          {{ getMontantTotal(echeance.referenceEcheancier) }}
                        </div>
                      </div>
                      <div class="column is-2"
                        v-if="!showEcheancier(echeance.referenceEcheancier)">
                          <p class="has-text-weight-bold right">
                            {{ $t('echeancierFacturation.tabs.datePrev') }}
                          </p>
                          <div class="right mg-10px">
                            {{ formatDate(echeance.dateDebut) }}
                          </div>
                      </div>
                      <div class="column is-1"
                        v-if="!showEcheancier(echeance.referenceEcheancier) && isNotRegie">
                          <p class="has-text-weight-bold right">
                            {{ $t('echeancierFacturation.tabs.aFacturer') }}
                          </p>
                          <div
                            class="right mg-10px">
                            {{ getAFacturer(echeance.referenceEcheancier) }}
                          </div>
                      </div>
                      <div class="column is-1" v-else></div>
                      <div class="column is-2"
                        v-if="!showEcheancier(echeance.referenceEcheancier)">
                          <p class="has-text-weight-bold right">
                            {{ $t('echeancierFacturation.tabs.commentaire') }}
                          </p>
                          <div class="right pdt-10px pdr-50px">
                            <b-tooltip
                            :label="echeance.echeancierLignes[0].commentaireFacturation"
                            multilined
                            type="is-primary"
                            position="is-bottom"
                            v-if="!!echeance.echeancierLignes[0].commentaireFacturation">
                            <b-icon
                                icon="information"
                                type="is-primary">
                            </b-icon>
                          </b-tooltip>
                          </div>
                      </div>
                    </div>
                </div>
            </template>
            <div class="card-content" v-if="showEcheancier(echeance.referenceEcheancier)">
              <div class="content">
                <div v-for="(payeur, index) in payeurs" :key="payeur.id">
                  <child-row-echeancier-facturation
                      :lignes="echeance.echeancierLignes.filter(l => l.tiersId === payeur.tiersId)"
                      :reference="echeance.referenceEcheancier"
                      :lignesWithReference="lignesWithReference(echeance.referenceEcheancier)"
                      :isNotRegie="isNotRegie"
                      :multiPayeur="isMultiPayeur"
                      :payeur="payeur"
                      :repartition="getValueRepartition(payeur)"
                      :montantFinal="getMontantFinal(payeurs, index, echeance)"
                      :dcf="dcf">
                  </child-row-echeancier-facturation>
                </div>
              </div>
            </div>
        </qd-collapse>
    </div>
  </div>
</template>

<script>
import getLibellesEcheancier from '@/services/commande/echeancierFacturation/getLibellesEcheancier';
import formatDate from '@/services/general/formatDate';
import childRowEcheancierFacturation from '@/components/devisCommandes/detailDevisCommande/childRowEcheancierFacturation';
import { formatDevise } from '@/services/general/filters/formatDevise';
import getLignesCommandesWithReferenceEcheancier
  from '@/services/commande/echeancierFacturation/getLignesCommandesWithReferenceEcheancier';
import { REGIE } from '@/configuration/societe/societe';
import { INTERVENANT_REPARTITION_TYPE, MONTANT } from '@/configuration/devisConf';

export default {
  props: {
    dcf: {
      type: Object,
      required: true,
    },
    isDevis: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFromBail: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      formatDate,
      constantes: {
        REGIE,
        MONTANT,
      },
    };
  },
  components: {
    childRowEcheancierFacturation,
  },
  computed: {
    payeurs() {
      return this.dcf.intervenants.filter(i => i.payeur);
    },
    echeanciers() {
      return this.dcf.echeanciers;
    },
    lignesCommande() {
      return this.dcf.lignesCommande;
    },
    isNotRegie() {
      return this.dcf.role !== this.constantes.REGIE && !this.isFromBail;
    },
    isMultiPayeur() {
      return this.payeurs.length > 1;
    },
    getTotalEcheanchiers() {
      let montant = 0;
      this.echeanciers.forEach((echeancier) => {
        if (echeancier.echeancierLignes.length === 0) return;
        montant += echeancier.echeancierLignes
          .map((ligne) => { return ligne.montant; })
          .reduce((montantTotal, montant) => montantTotal + montant);
      });
      return montant;
    },
  },
  methods: {
    getQuotePart(payeur) {
      const repartitionTypeLibelle = this.$i18n.t(
        INTERVENANT_REPARTITION_TYPE.find((repartition) => {
          return repartition.code === payeur.typeRepartition;
        }).libelle,
      );
      return payeur.typeRepartition === MONTANT
        ? formatDevise(payeur.repartition)
        : payeur.repartition + ' ' + repartitionTypeLibelle;
    },
    showEcheancier(reference) {
      return this.dcf.echeanciers
        .find(e => e.referenceEcheancier === reference).quantitePeriodicite > 0
      || this.payeurs.map(payeur => this.dcf.echeanciers
        .find(e => e.referenceEcheancier === reference)
        .echeancierLignes
        .filter(ligne => ligne.tiersId === payeur.tiersId))
        .some(echs => echs.some(e => !!e.factureId || e.facturationFaite) || echs.length > 1);
    },
    getMontantFinal(array, index, echeance) {
      if (index === array.length - 1) {
        const mf = {
          montantlignes: this.getLignesMontantTotal(echeance.echeancierLignes),
          montantAll: this.getMontantTotal(echeance.referenceEcheancier),
        };
        return mf;
      }
      return null;
    },
    getValueRepartition(payeur) {
      if (payeur.typeRepartition === MONTANT) {
        if (!this.dcf.montantHt) return 0;
        return (payeur.repartition * 100) / this.dcf.montantHt;
      }
      return payeur.repartition;
    },
    lignesWithReference(reference) {
      return getLignesCommandesWithReferenceEcheancier(
        this.lignesCommande,
        reference,
      );
    },
    getLibelle(referenceEcheancier) {
      return getLibellesEcheancier(referenceEcheancier, this.lignesCommande);
    },
    getDate(date) {
      return this.$i18n.t('echeancierFacturation.tabs.datePatri', { date: formatDate(date) });
    },
    getMontantTotal(reference) {
      const lignes = this.lignesWithReference(reference)
        .map((ligne) => { return ligne.totalHtAvecLigneRemise; });
      const total = lignes.reduce((a, montant) => a + montant, 0);
      return formatDevise(total);
    },
    getLignesMontantTotal(lignes) {
      return formatDevise(lignes.reduce((a, l) => a + l.montant, 0));
    },
    getFournisseur(reference) {
      if (!this.lignesWithReference(reference).every((ligne) => {
        return ligne.fournisseurId === this.lignesWithReference(reference)[0].fournisseurId;
      })) return this.$i18n.t('detail.schedule.table.valDiff');
      return this.lignesWithReference(reference)[0]?.fournisseur ? this.lignesWithReference(reference)[0].fournisseur.nomCommercial : '';
    },
    getAFacturer(reference) {
      const aFacturerBase = this.lignesWithReference(reference)[0]?.aFacturer;
      if (!this.lignesWithReference(reference).every((ligne) => {
        return ligne.aFacturer === aFacturerBase;
      })) return this.$i18n.t('detail.schedule.table.valDiff');
      return aFacturerBase ? this.$i18n.t('general.oui') : this.$i18n.t('general.non');
    },
    getMontantPayeur(payeur) {
      if (payeur.typeRepartition === MONTANT) return payeur.repartition;
      return formatDevise((payeur.repartition * this.dcf.montantHt) / 100);
    },
  },
};
</script>
